/**
 * Decouples ChatWindow's WebSocket connection from siblings (TasksTab,
 * MessageBubble's ResolverChoice) that need to send messages into the
 * agent loop without prop-drilling or lifting the whole `useChat` hook
 * out of ChatWindow.
 *
 * Lifecycle: ChatWindow's `useChat` registers `send` (bead-forward),
 * `resolverSend` (resolver-choice), `sopSend` (sop-ingestion) and
 * `referenceSend` (reference-ingestion) on mount, clears on unmount.
 * Sibling components read reactively; when the slot is `null` (WS not
 * connected or chat not mounted), they disable themselves.
 *
 * `resolvedContexts` is session-scoped — a Set of `ResolverContext`
 * tokens the user already answered. ResolverChoice consults this to
 * stay disabled across re-renders within the session. Full-page reload
 * loses the set (a `[RESOLVER_OPTIONS]` tag in persisted history will
 * become clickable again); v1 accepts this — persistent resolution
 * state would require a Convex row, which is out of quick-spec scope.
 *
 * Why a store and not React Context: ChatWindow and TaskPanel are siblings,
 * not parent/child — a Provider would have to wrap their common ancestor
 * (ThreadView), which would force ThreadView to host the WS. The store
 * sidesteps that: each component opts in.
 */

import { create } from "zustand";

import type { BeadValueObject } from "../../domain/praxis-beads";

export interface ResolverChoiceArgs {
  readonly context: string;
  readonly choice: "A" | "B" | "C";
  readonly userText?: string;
}

interface ChatBridgeState {
  readonly send: ((bead: BeadValueObject) => void) | null;
  readonly resolverSend: ((args: ResolverChoiceArgs) => void) | null;
  readonly sopSend: ((files: File[]) => Promise<void>) | null;
  readonly referenceSend: ((files: File[]) => Promise<void>) | null;
  readonly resolvedContexts: ReadonlySet<string>;
  // Mirrored from useChat: true while the web-chat agent loop has an
  // in-flight turn (streaming tokens or awaiting first chunk). Consumed
  // by sibling-panel widgets that share the /ws/chat surface and want
  // to gate themselves against firing a second turn on top of the
  // first. ResolverChoice reads this; bead-forward / sop-ingestion do
  // not (intentional power-user back doors, see frontend-doctrine §4.3).
  readonly isAgentActive: boolean;
  setSend: (fn: (bead: BeadValueObject) => void) => void;
  clearSend: () => void;
  setResolverSend: (fn: (args: ResolverChoiceArgs) => void) => void;
  clearResolverSend: () => void;
  setSopSend: (fn: (files: File[]) => Promise<void>) => void;
  clearSopSend: () => void;
  setReferenceSend: (fn: (files: File[]) => Promise<void>) => void;
  clearReferenceSend: () => void;
  setIsAgentActive: (next: boolean) => void;
  markResolved: (context: string) => void;
}

export const useChatBridgeStore = create<ChatBridgeState>((set) => ({
  send: null,
  resolverSend: null,
  sopSend: null,
  referenceSend: null,
  resolvedContexts: new Set<string>(),
  isAgentActive: false,
  setSend: (fn) => set({ send: fn }),
  clearSend: () => set({ send: null }),
  setResolverSend: (fn) => set({ resolverSend: fn }),
  clearResolverSend: () => set({ resolverSend: null }),
  setSopSend: (fn) => set({ sopSend: fn }),
  clearSopSend: () => set({ sopSend: null }),
  setReferenceSend: (fn) => set({ referenceSend: fn }),
  clearReferenceSend: () => set({ referenceSend: null }),
  setIsAgentActive: (next) => set({ isAgentActive: next }),
  markResolved: (context) =>
    set((state) => {
      const next = new Set(state.resolvedContexts);
      next.add(context);
      return { resolvedContexts: next };
    }),
}));
